<template>
  <div class="bundle-part-container" :id="'bundlePart-' + bundlePart.id">
    <div class="bundle-part-header">
      <div class="bundle-part-title-row">
        <span class="bundle-part-name" :class="{'error-text': errors.length}">{{ bundlePart.name }}</span>
        <span class="supplement-price" v-if="bundlePart.supplementPrice && bundlePart.supplementPrice.value > 0">
          {{ $t('cart.bundle.supplement_short') }} {{ formatPrice(bundlePart.supplementPrice.value) }}
        </span>
      </div>
      <small class="error-message animate__animated animate__headShake" v-for="error in errors" :key="error.type">
        {{ error.message }}
      </small>

      <span class="bundle-part-description" v-if="bundlePart.min === 0">
        {{ $t('cart.bundle.optional_max', {count: bundlePart.max}) }}
      </span>
      <span class="bundle-part-description" v-else-if="bundlePart.min > 0 && bundlePart.min === bundlePart.max">
        {{ $t('cart.bundle.required_exact', { count: bundlePart.min }) }}
      </span>
      <span class="bundle-part-description" v-else-if="bundlePart.min > 0 && bundlePart.min !== bundlePart.max">
        {{ $t('cart.bundle.required_range', { min: bundlePart.min, max: bundlePart.max }) }}
      </span>
    </div>

    <div v-for="bundlePartItem in bundlePart.bundlePartItems" :key="bundlePartItem.id">
      <div class="bundle-item-row">
        <div v-if="bundlePart.max > 1 || bundlePart.min === 0" style="display: flex; align-items: center; justify-items: center">
          <input
            :disabled="!bundlePartItem.product.avaible"
            type="checkbox"
            @change="inputSelect"
            v-model="inputSelected"
            :value="bundlePartItem.id"
            :name="'check_' + bundlePart.id"
            :id="bundlePart.id + '_' + bundlePartItem.id"
            class="form-checkbox">
        </div>
        <div v-else-if="bundlePart.max === 1" style="display: flex; align-items: center; justify-items: center">
          <input
            :disabled="!bundlePartItem.product.avaible"
            type="radio"
            @change="inputSelect"
            v-model="inputSelected"
            :value="bundlePartItem.id"
            :name="'radio_' + bundlePart.id"
            :id="bundlePart.id + '_' + bundlePartItem.id"
            class="form-radio">
        </div>
        <label :for="bundlePart.id + '_' + bundlePartItem.id" class="bundle-item-label" :class="{'unavailable': !bundlePartItem.product.avaible}">
          {{ bundlePartItem.product.name }}
        </label>

        <span v-if="!bundlePartItem.product.avaible" class="unavailable-text">
          {{ $t('system.unavailable') }}
        </span>
        <span v-else-if="bundlePartItem.supplementPrice && bundlePartItem.supplementPrice.value" class="item-price">
          +{{ formatPrice(bundlePartItem.supplementPrice.value) }}
        </span>
      </div>

      <div class="options-button"
           @click="openProductEditor(bundlePartItem)"
           v-if="isItemSelected(bundlePartItem.id) && bundlePartItem.product.options && bundlePartItem.product.options.length > 0">

        <span class="options-text options-selected" v-if="getItemModifiers(bundlePartItem.id) && getItemModifiers(bundlePartItem.id).length > 0">
          {{ $tc('cart.bundle.selections_made', getItemModifiers(bundlePartItem.id).length) }}
        </span>
        <span class="options-text options-required" v-else-if="hasRequiredOptions(bundlePartItem.product)">
          {{ $tc('cart.bundle.selection_required', getRequiredOptionsCount(bundlePartItem.product)) }}
        </span>
        <span class="options-text options-possible" v-else>
          {{ $tc('cart.bundle.selections_possible', bundlePartItem.product.options.length) }}
        </span>

        <i class="material-icons options-chevron">chevron_right</i>
      </div>
    </div>

  </div>
</template>

<script>
import { $vfm } from 'vue-final-modal';
import BundleProductOptions from '@/components/Modals/Product/BundleProductOptions.vue';

export default {
  name: 'BundlePart',
  props: {
    bundlePart: {
      type: Object,
      required: true
    },
    value: {
      type: Array,
      default: () => []
    },
    externalError: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      inputSelected: [],
      selectedBundlePartItemModifiers: [],
      errors: [],
      currentValue: null,
      isUpdating: false
    }
  },
  methods: {
    inputSelect(e) {
      // Transform all data to array, and ensure that we never have more than the max selections
      if (Array.isArray(this.inputSelected)) { // From checkboxes
        if (e && this.inputSelected.length > this.bundlePart.max && this.bundlePart.max === 1) {
          this.inputSelected = [e.target.value]; // Switch to single selection
        }
        else if (this.inputSelected.length > this.bundlePart.max) {
          this.inputSelected = this.inputSelected.filter(selected => selected !== e.target.value); // Limit to max, no more selection...
        }
      } else { // From radio
        this.inputSelected = [this.inputSelected];
      }

      // Clear modifier groups for unselected items
      const selectedIds = Array.isArray(this.inputSelected) ? this.inputSelected : [this.inputSelected].filter(Boolean);
      this.selectedBundlePartItemModifiers = this.selectedBundlePartItemModifiers.filter(mod =>
        selectedIds.includes(mod.bundlePartItemId)
      );

      // Update model
      this.update();
      // Only validate if there are already errors or if external error exists
      if (this.errors.length > 0 || this.externalError) {
        this.validate();
      }
    },

    openProductEditor(bundlePartItem) {
      // First check selectedBundlePartItemModifiers (for new selections)
      let modifierGroup = this.selectedBundlePartItemModifiers.find(mod =>
        mod.bundlePartItemId === bundlePartItem.id
      );
      let defaultModifiers = modifierGroup ? modifierGroup.modifiers : [];

      // If no modifiers found, check if there are existing modifiers from value prop (for edit mode)
      if (defaultModifiers.length === 0 && this.value && this.value.length > 0) {
        const existingSelection = this.value.find(selection =>
          selection.bundlePartItem && selection.bundlePartItem.id === bundlePartItem.id
        );
        if (existingSelection && existingSelection.modifiers) {
          defaultModifiers = existingSelection.modifiers;

          // Also update selectedBundlePartItemModifiers to keep consistency
          this.selectedBundlePartItemModifiers.push({
            bundlePartItemId: bundlePartItem.id,
            modifiers: defaultModifiers
          });
        }
      }

      // Open modal directly with dynamic component approach
      $vfm.show({
        component: BundleProductOptions,
        bind: {
          product: bundlePartItem.product,
          bundlePart: this.bundlePart,
          bundlePartItem: bundlePartItem,
          defaultModifiers: defaultModifiers
        },
        on: {
          confirm: (processedOptions) => {
            // Update modifiers directly in this component
            this.updateItemModifiers(bundlePartItem.id, processedOptions);
            // The modal will close itself in BundleProductOptions component
          }
        }
      });
    },

    updateItemModifiers(bundlePartItemId, modifiers) {
      // Remove existing modifiers for this item
      this.selectedBundlePartItemModifiers = this.selectedBundlePartItemModifiers.filter(mod =>
        mod.bundlePartItemId !== bundlePartItemId
      );

      // Add new modifiers
      if (modifiers && modifiers.length > 0) {
        this.selectedBundlePartItemModifiers.push({
          bundlePartItemId: bundlePartItemId,
          modifiers: modifiers
        });
      }

      // Update the model
      this.update();
    },

    update() {
      if (this.isUpdating) return; // Éviter la récursion

      this.isUpdating = true;

      const selectedList = [];
      const selectedIds = Array.isArray(this.inputSelected) ? this.inputSelected : [this.inputSelected].filter(Boolean);

      selectedIds.forEach(selectedId => {
        const bundlePartItem = this.bundlePart.bundlePartItems.find(item => item.id == selectedId);
        const modifierGroup = this.selectedBundlePartItemModifiers.find(mod =>
          mod.bundlePartItemId == selectedId
        );
        const modifiers = modifierGroup ? modifierGroup.modifiers : [];

        if (bundlePartItem) {
          selectedList.push({
            bundlePart: this.bundlePart,
            bundlePartItem: bundlePartItem,
            product: bundlePartItem.product,
            modifiers: modifiers,
            quantity: 1,
            supplementPrice: (this.bundlePart.supplementPrice ? this.bundlePart.supplementPrice.value : 0) + (bundlePartItem.supplementPrice ? bundlePartItem.supplementPrice.value : 0)
          });
        }
      });

      // Mettre à jour currentValue pour éviter la boucle du watcher
      this.currentValue = selectedList;

      this.$emit('input', selectedList);
      this.$emit('update', {
        bundlePart: this.bundlePart,
        selection: selectedList
      });

      this.$nextTick(() => {
        this.isUpdating = false;
      });
    },

    validate() {
      this.errors = [];

      // Add external error if provided (prioritize external error over internal validation)
      if (this.externalError) {
        this.errors.push({
          type: 'external_error',
          message: this.externalError
        });
        return this.errors.length === 0;
      }

      // Internal validation only if no external error
      if (this.bundlePart.required && this.bundlePart.min > 0) {
        if (this.inputSelected.length < this.bundlePart.min) {
          this.errors.push({
            type: 'min_required',
            message: this.$t('errors.bundle_min_required', { min: this.bundlePart.min, name: this.bundlePart.name })
          });
        }
      }

      if (this.inputSelected.length > this.bundlePart.max) {
        this.errors.push({
          type: 'max_exceeded',
          message: this.$t('errors.bundle_max_exceeded', { max: this.bundlePart.max, name: this.bundlePart.name })
        });
      }

      // Check for required options on selected items
      this.inputSelected.forEach(selectedId => {
        const bundlePartItem = this.bundlePart.bundlePartItems.find(item => item.id == selectedId);
        if (bundlePartItem && this.hasRequiredOptions(bundlePartItem.product)) {
          const itemModifiers = this.getItemModifiers(selectedId);
          if (!itemModifiers || itemModifiers.length === 0) {
            this.errors.push({
              type: 'required_options',
              message: this.$t('errors.bundle_required_options', { item: bundlePartItem.product.name })
            });
          }
        }
      });

      return this.errors.length === 0;
    },

    isItemSelected(itemId) {
      const selectedIds = Array.isArray(this.inputSelected) ? this.inputSelected : [this.inputSelected].filter(Boolean);
      return selectedIds.includes(itemId);
    },

    getItemModifiers(itemId) {
      const modifierGroup = this.selectedBundlePartItemModifiers.find(mod =>
        mod.bundlePartItemId == itemId
      );
      return modifierGroup ? modifierGroup.modifiers : [];
    },

    hasRequiredOptions(product) {
      return product.options && product.options.some(option => option.minSelect > 0);
    },

    getRequiredOptionsCount(product) {
      if (!product.options) return 0;
      return product.options.filter(option => option.minSelect > 0).length;
    },

    formatPrice(priceInCents) {
      return new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: 'EUR'
      }).format(priceInCents / 100);
    },

    initDefaults() {
      if (this.isUpdating) return; // Éviter la récursion

      if (this.value && this.value.length > 0) {
        // Clear existing data first
        this.selectedBundlePartItemModifiers = [];
        this.inputSelected = [];

        this.value.forEach(selection => {
          if (this.bundlePart.max === 1 && this.bundlePart.min === 1) {
            this.inputSelected = selection.bundlePartItem.id;
          } else {
            if (!Array.isArray(this.inputSelected)) this.inputSelected = [];
            this.inputSelected.push(selection.bundlePartItem.id);
          }

          // Always add modifiers if they exist, even if empty array
          if (selection.modifiers) {
            // Remove any existing entry for this bundlePartItem to avoid duplicates
            this.selectedBundlePartItemModifiers = this.selectedBundlePartItemModifiers.filter(mod =>
              mod.bundlePartItemId !== selection.bundlePartItem.id
            );

            this.selectedBundlePartItemModifiers.push({
              bundlePartItemId: selection.bundlePartItem.id,
              modifiers: selection.modifiers
            });
          }
        });

        // Check if existing selection has errors (for edit mode)
        this.checkForExistingErrors();
        // Ne pas appeler update() ici pour éviter la boucle
      } else {
        // Reset selections si pas de valeur
        this.inputSelected = [];
        this.selectedBundlePartItemModifiers = [];
      }
    },

    checkForExistingErrors() {
      // Only check for internal errors if there's no external error
      if (this.externalError) {
        return;
      }

      // Check if current selection violates bundle part rules
      const currentSelectionCount = Array.isArray(this.inputSelected) ? this.inputSelected.length : (this.inputSelected ? 1 : 0);

      let hasErrors = false;

      // Check minimum requirements
      if (this.bundlePart.required && this.bundlePart.min > 0) {
        if (currentSelectionCount < this.bundlePart.min) {
          hasErrors = true;
        }
      }

      // Check maximum limits
      if (currentSelectionCount > this.bundlePart.max) {
        hasErrors = true;
      }

      // Check for required options on selected items
      const selectedIds = Array.isArray(this.inputSelected) ? this.inputSelected : [this.inputSelected].filter(Boolean);
      selectedIds.forEach(selectedId => {
        const bundlePartItem = this.bundlePart.bundlePartItems.find(item => item.id == selectedId);
        if (bundlePartItem && this.hasRequiredOptions(bundlePartItem.product)) {
          const itemModifiers = this.getItemModifiers(selectedId);
          if (!itemModifiers || itemModifiers.length === 0) {
            hasErrors = true;
          }
        }
      });

      // Only validate if there are errors in existing selection
      if (hasErrors) {
        this.validate();
      }
    }
  },
  watch: {
    value: {
      handler(newValue) {
        // Éviter la boucle infinie en comparant les valeurs
        if (JSON.stringify(newValue) !== JSON.stringify(this.currentValue)) {
          this.currentValue = newValue;
          this.initDefaults();
        }
      },
      immediate: true
    },
    externalError: {
      handler(newError) {
        // Only validate when there's an actual error, not when clearing
        if (newError) {
          this.validate();
        } else {
          // Clear errors when external error is cleared
          this.errors = this.errors.filter(error => error.type !== 'external_error');
        }
      },
      immediate: false
    }
  },
  mounted() {
    this.initDefaults();
  }
}
</script>

<style lang="scss">
.bundle-part-container {
  padding: 0.2rem 1.5rem;
  margin-top: 1rem;
}

.bundle-part-header {
  display: flex;
  flex-direction: column;
  margin-bottom: 0.5rem;
  line-height: 1.3;
}

.bundle-part-title-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
}

.bundle-part-name {
  font-weight: 600;
  flex: 1;

  &.error-text {
    color: #b91c1c;
  }
}

.supplement-price {
  font-size: 0.75rem;
  color: #525252;
}

.error-message {
  font-size: 0.75rem;
  color: #b91c1c;
  font-weight: 500;
}

.bundle-part-description {
  color: #9ca3af;
  margin-top: 0.25rem;
  font-size: 0.75rem;
}

// Bundle Items
.bundle-item-row {
  display: flex;
  align-items: center;
  padding: 0.3rem 0;
}

.bundle-item-label {
  flex: 1;
  padding-left: 0.5rem;
  margin: 0 !important;
  cursor: pointer;

  &.unavailable {
    opacity: 0.5;
  }
}

.unavailable-text {
  font-size: 0.75rem;
  text-transform: uppercase;
  color: #9ca3af;
}

.item-price {
  color: #4b5563;
  font-size: 0.875rem;
  padding-left: 0.5rem;
}

// Options Button
.options-button {
  height: 2.75rem;
  width: 100%;
  background-color: #f5f5f5;
  border-radius: 0.375rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-left: 1rem;
  padding-right: 1rem;
  margin-bottom: 0.5rem;
  cursor: pointer;
}

.options-text {
  font-weight: 500;
  font-size: 0.875rem;

  &.options-selected {
    color: #262626;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &.options-required {
    color: #dc2626;
  }

  &.options-possible {
    color: #a3a3a3;
  }
}

.options-chevron {
  color: #525252;
}

</style>
